import {
  combineLatest,
  distinctUntilChanged,
  map,
  type Observable,
} from 'rxjs';
import {
  hasAnswerSurveysOwnerQuestion,
  isSuitableAnswerSurveysSurvey,
} from '@ardoq/product-tours';
import type { APISurveyAttributes } from '@ardoq/api-types';
import { surveyAccessControlInterface } from 'resourcePermissions/accessControlHelpers/surveys';
import { featureContext$ } from 'streams/featureContext$';
import { surveys$ } from 'streams/surveys/surveys$';

const isFoundationSurvey = ({ name }: APISurveyAttributes): boolean =>
  /FND/i.test(name ?? '');

const isSuitableFoundationSurvey = (survey: APISurveyAttributes): boolean =>
  isFoundationSurvey(survey) && isSuitableAnswerSurveysSurvey(survey);

const isSuitableWithOwnerQuestion = (survey: APISurveyAttributes): boolean =>
  isSuitableAnswerSurveysSurvey(survey) &&
  hasAnswerSurveysOwnerQuestion(survey);

const isSuitableFoundationSurveyWithOwnerQuestion = (
  survey: APISurveyAttributes
): boolean => isFoundationSurvey(survey) && isSuitableWithOwnerQuestion(survey);

export type CanAnswerSurvey = (survey: APISurveyAttributes) => boolean;

export const selectTourSurvey = (
  surveys: APISurveyAttributes[],
  canAnswerSurvey: CanAnswerSurvey
): APISurveyAttributes | null => {
  const answerable = surveys.filter(
    survey => canAnswerSurvey(survey) && !survey.misc?.disableLandingPage
  );
  return (
    answerable.find(isSuitableFoundationSurveyWithOwnerQuestion) ??
    answerable.find(isSuitableWithOwnerQuestion) ??
    answerable.find(isSuitableFoundationSurvey) ??
    answerable.find(isSuitableAnswerSurveysSurvey) ??
    answerable[0] ??
    null
  );
};

export const answerSurveysTourSurvey$: Observable<APISurveyAttributes | null> =
  combineLatest([surveys$, featureContext$]).pipe(
    map(([surveys, featureContext]) =>
      selectTourSurvey(surveys.list, candidate =>
        surveyAccessControlInterface.canSubmitSurvey(featureContext, candidate)
      )
    ),
    distinctUntilChanged()
  );
